import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button, Col, Container, Form, FormGroup, InputGroup, Label, Row } from "reactstrap";
import requestApi from "../../services/requestApi.service";
import { setSessionValue, showToast, isMobile, getEncodeedLicense, parseJSON, setLicenseErrorStatus } from "../../Common/Appcommon";
import { useAppContext } from "../../Context/AppContext";

const CheckoutForm = forwardRef((props, ref) => {
    let { isUsagePage, selectedPlan, paymentDetails, billingDetails, noofMonths, isAgency, plan_type } = props;
    const inputRef = useRef()
    if(selectedPlan && selectedPlan.type){
        let type = selectedPlan.type;
        type = parseJSON(type);
        if(type && type.value){
            selectedPlan.plan_type = type.value;
        }
    }
    const stripe = useStripe();
    const elements = useElements();
    const [cardDetails, setCardDetails] = useState({});
    const [showCardElement, setShowCardElement] = useState(true);
    const [error, setError] = useState(null);
    const { theme, subCurrency, agencyCurrency, setShowHeaderNoti, setShowPopupNoti } = useAppContext();
    useEffect(() => {
        if (paymentDetails && (paymentDetails["status"] === "Active" || (paymentDetails["upcoming_plans"] && paymentDetails["upcoming_plans"].length > 0))) {
            let plan_type = paymentDetails["plan_type"] ?? "Subscription";
            let plan_cost = parseInt(paymentDetails["plan_cost"]) ?? 0;
            if(plan_type !== "Subscription" || (plan_type === "Subscription" && plan_cost > 0)){
                getCardDetails();
                setShowCardElement(false);
            }
        }
    }, [paymentDetails]);

    const getCardDetails = async () => {
        const params = {
            ENTITY: "STRIPE_PROCESSOR",
            OPERATION: "GET_CARD_DETAILS",
            isAgency: isAgency,
            isOtherApiReq: true,
        };
        try {
            let response = await requestApi.processServerRequest(params);
            if (response.issuccess) {
                let data = response.data;
                setCardDetails(data);
            }
        } catch (error) {
            console.error("Error fetching plan pricing:", error);
        }
    }
    
    useImperativeHandle(ref, () => ({
        processPayment,
    }));

    /**
     * @function processPayment send to stripe via api
     * @param {*} event event trigger by on submit
     * @returns {void}
     */
    const processPayment = async () => {
        //event.preventDefault();
        // if manage subscription update without card edit
        if(!showCardElement && isUsagePage){
            return true;
        }

        if(showCardElement){
            const cardElement = elements.getElement(CardNumberElement);
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: billingDetails?.Name ?? "",
                    email: billingDetails?.Email_id ?? "",
                }
            });
            if (error) {
                setError(error.message);
                if(inputRef.current){
                    inputRef.current.focus();
                }
                return;
            }
            const params = {
                ENTITY: "STRIPE_PROCESSOR",
                OPERATION: "CREATE_AUTH_INTENT",
                paymentMethodId: paymentMethod.id,
                billingDetails: billingDetails,
                selectedPlan: selectedPlan,
                noofMonths: noofMonths,
                isAgency: isAgency,
                isAuthOnly: showCardElement && isUsagePage,
                isOtherApiReq: true,
                currencysymbol: isAgency ? agencyCurrency : subCurrency,
                isAuthNeeded: selectedPlan?.plan_type === "pay_as_you_go"
            };
            try {
                let response = await requestApi.processServerRequest(params);
                if (response.issuccess) {
                    let data = response.data;
                    let client_secret = data.client_secret ?? "";
                    // Confirm the Payment Intent to authorize the payment
                    const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(client_secret);
                    if (confirmError) {
                        setError(confirmError.message);
                        if(inputRef.current){
                            inputRef.current.focus();
                        }
                    } else {
                        // Authorization was successful, proceed with capturing later
                        return capturePayment(paymentIntent.id);
                    }
                }
            } catch (error) {
                console.error("Error fetching plan pricing:", error);
            }
        }else{
            return capturePayment();
        }
        return false;
    };

    /**
     * 
     * @param {String} paymentIntentId paymentid from stripe API
     * @returns {void}
     */
    const capturePayment = async (paymentIntentId = "") => {
        const params = {
            ENTITY: "STRIPE_PROCESSOR",
            OPERATION: "CAPTURE_PAYMENT_INTENT",
            paymentIntentId: paymentIntentId,
            billingDetails: billingDetails,
            selectedPlan: selectedPlan,
            noofMonths: noofMonths,
            isAuthOnly: isUsagePage,
            isAgency: isAgency,
            isOtherApiReq: true,
            currencysymbol: isAgency ? agencyCurrency : subCurrency,
            isAuthNeeded: selectedPlan?.plan_type === "pay_as_you_go"
        };
        let response = await requestApi.processServerRequest(params, "WebV2", "post", false, 30000);
        if(response.issuccess && response.data){
            if(isUsagePage){
                showToast("Method Change successfully");
            }else{
                setLicenseErrorStatus("header_noti", "false");
                setShowHeaderNoti(false);
                setShowPopupNoti(false);
                setSessionValue("customer_type", "Customer")
            }
            await getEncodeedLicense();
            return true;
        }
        return false;
    }

    const handleCardEditCall = () => {
        setShowCardElement(!showCardElement);
    }
    
    const elementStyle = {
        base: {
            color: theme === "dark-theme" ? "#ffffff99" : "", 
        },
        invalid: {
          color: "#fa755a",
        },
    };

    let exp_year = cardDetails.exp_year
    if(exp_year){
        exp_year = cardDetails.exp_year.toString().slice(-2);
    }
    let exp_month = cardDetails.exp_month;
    if(exp_month < 10){
        exp_month = 0 + ""+ exp_month;
    }

    let style = theme === "dark-theme" ? {
        border: "1px solid #313135",
        borderRadius: "4px",
        padding: "10px",
    } : {
        border: "1px solid #e1e1e1",
        borderRadius: "4px",
        padding: "10px",
    }

    return (
        <Container className="checkout-box-container">
            <Form>
                <Row className="checkout-grp">
                    <Col md={12}>
                        <FormGroup className="input-group-text-box">
                            {
                                showCardElement ? (
                                    <>
                                        <Row lg={12} className="mt-3">
                                            <Col lg={12}>
                                                <label className="form-label mt-3">Card number</label>
                                                <div style={style} ref={inputRef} tabIndex={-1}>
                                                    <CardNumberElement options={{ style: elementStyle, showIcon: true, iconStyle: "solid" }} />
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col lg={6}>
                                                <label className="form-label mt-3">Expiry date</label>
                                                <div style={style}>
                                                <CardExpiryElement options={{ style: elementStyle }} />
                                                </div>
                                            </Col>
                                            <Col lg={6}>
                                                <label className="form-label mt-3">Security code</label>
                                                <div style={style}>
                                                <CardCvcElement options={{ style: elementStyle, showIcon: true, iconStyle: "solid" }} />
                                                </div>
                                            </Col>
                                        </Row>
                                    </>
                                ) : (
                                    <>
                                    {
                                        !!cardDetails.last4 &&
                                        <>
                                            <Label>Card Details</Label>
                                            <InputGroup >
                                                {
                                                    isMobile() ? (
                                                        <>
                                                            <div className="input-group-text">
                                                                <span>{cardDetails.brand}</span>
                                                            </div>
                                                            <div className="input-group-text input-group-text-width">
                                                                <div className="checkout-span-group">
                                                                    <span>... **** {cardDetails.last4}</span>
                                                                </div>
                                                                <span className="ms-auto">{exp_month}/{exp_year}</span>
                                                            </div>
                                                            <div className="input-group-text cursor-pointer">
                                                                <i class="fi fi-rc-pencil" onClick={() => handleCardEditCall()}></i>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="input-group-text">
                                                                <i class="fi fi-rr-credit-card"></i>
                                                            </div>
                                                            <div className="input-group-text input-group-text-width">
                                                                <div className="checkout-span-group">
                                                                    <span>{cardDetails.brand}</span>
                                                                    <span>**** **** **** {cardDetails.last4}</span>
                                                                    <span> </span>
                                                                </div>
                                                                <span className="ms-auto"> {exp_month}/{exp_year}</span>
                                                            </div>
                                                            <div className="input-group-text cursor-pointer">
                                                                <i class="fi fi-rc-pencil" onClick={() => handleCardEditCall()}></i>
                                                            </div>
                                                        </>
                                                    )
                                                }
                                            </InputGroup>
                                        </>
                                    }
                                    </>
                                )
                            }
                            { 
                                showCardElement && Object.keys(cardDetails).length > 0 &&
                                <Button className="theme-bg-g mt-2" onClick={() => handleCardEditCall()}>
                                    Cancel
                                </Button>
                            }
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col className="text-center m-2">
                        {error && <div className=" alert alert-danger">{error}</div>}
                    </Col>
                </Row>
            </Form>
        </Container>
    );
});

export default CheckoutForm;